import {
  Button,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text
} from '@chakra-ui/react'
import React from 'react'
import ChevronDown from '../../../components/icons/chevron-down'
import ChevronLeft from '../../../components/icons/chevron-left'
import ChevronRight from '../../../components/icons/chevron-right'
import { CalendarBlankIcon } from '../../../icons/calendar/calendar-blank-icon'
import { CalendarTodayIcon } from '../../../icons/calendar/calendar-today-icon'
import SliderIcon from '../../../icons/calendar/slider-icon'
import { CalendarView } from './full-size-calendar'

interface FullSizeHeaderProps {
  isMobile: boolean
  navigationDate: any
  selectedView: CalendarView
  showSidebarMenuButton: boolean
  onSidebarMenuClick?: () => void
  previousButtonClick: () => void
  nextButtonClick: () => void
  todayButtonClick: () => void
  handleViewChange: (view: CalendarView) => void
  btnRef: React.RefObject<any>
}

export const FullSizeHeader: React.FC<FullSizeHeaderProps> = ({
  isMobile,
  navigationDate,
  selectedView,
  showSidebarMenuButton,
  onSidebarMenuClick,
  previousButtonClick,
  nextButtonClick,
  todayButtonClick,
  handleViewChange,
  btnRef
}) => {
  if (isMobile) {
    return (
      <Flex
        position="relative"
        align="center"
        mb={3}
        mt={3}
        wrap="nowrap"
        height="40px"
        zIndex={2}
        px={2}
      >
        {/* Left group: sidebar and prev */}
        <Flex align="center" gap={1} minWidth="0">
          <IconButton
            ref={btnRef}
            icon={<SliderIcon />}
            aria-label="Open sidebar menu"
            onClick={onSidebarMenuClick}
            variant="outline"
            borderRadius="4px"
            minW="32px"
            h="32px"
            px="10px"
            bg="transparent"
            border="1px solid #373C4123"
            _hover={{ bg: 'rgba(45,84,232,0.04)', color: '#2D54E8' }}
            _focus={{ boxShadow: 'none' }}
          />
        </Flex>
        {/* Center: Month/Year */}
        <Flex
          position="absolute"
          left="0"
          right="0"
          justify="center"
          align="center"
          pointerEvents="none"
        >
          <IconButton
            aria-label="Previous"
            icon={<ChevronLeft width={12} height={12} />}
            onClick={previousButtonClick}
            variant="ghost"
            minW="32px"
            h="32px"
            w="32px"
            p={0}
            _hover={{ bg: 'rgba(45,84,232,0.04)', color: '#2D54E8' }}
            _focus={{ boxShadow: 'none' }}
            border="none"
            pointerEvents="auto"
          />
          <Text
            as="h4"
            textAlign="center"
            px={1}
            isTruncated
            minWidth={0}
            pointerEvents="auto"
            fontSize="18px"
            fontStyle="normal"
            color="#282828"
          >
            <span
              style={{ fontWeight: 600, fontVariantNumeric: 'lining-nums' }}
            >
              {navigationDate.format('MMM YYYY')}
            </span>{' '}
            <span
              style={{ fontWeight: 400, fontVariantNumeric: 'lining-nums' }}
            >
              Calendar
            </span>
          </Text>
          <IconButton
            aria-label="Next"
            icon={<ChevronRight width={12} height={12} />}
            onClick={nextButtonClick}
            variant="ghost"
            minW="32px"
            h="32px"
            w="32px"
            p={0}
            _hover={{ bg: 'rgba(45,84,232,0.04)', color: '#2D54E8' }}
            _focus={{ boxShadow: 'none' }}
            border="none"
            pointerEvents="auto"
          />
        </Flex>
        {/* Right group: today, view selector */}
        <Flex align="center" gap={1} minWidth="0" ml="auto">
          <IconButton
            aria-label="Jump to today"
            icon={<CalendarTodayIcon width={16} height={16} />}
            onClick={todayButtonClick}
            variant="outline"
            borderRadius="4px"
            minW="32px"
            h="32px"
            px="10px"
            bg="transparent"
            border="1px solid #373C4123"
            _hover={{ bg: 'rgba(45,84,232,0.04)', color: '#2D54E8' }}
            _focus={{ boxShadow: 'none' }}
          />
          <Menu isLazy>
            <MenuButton
              as={IconButton}
              aria-label="View selector"
              icon={<CalendarBlankIcon width={16} height={16} />}
              variant="outline"
              borderRadius="4px"
              minW="32px"
              h="32px"
              px="10px"
              bg="transparent"
              border="1px solid #373C4123"
              _hover={{ bg: 'rgba(45,84,232,0.04)' }}
              _focus={{ boxShadow: 'none' }}
            />
            <Portal>
              <MenuList
                boxShadow="lg"
                borderRadius="md"
                minW="120px"
                p={0}
                zIndex={9999}
                fontFamily='"Helvetica Neue", Helvetica, Arial, sans-serif'
              >
                <MenuItem
                  onClick={() => handleViewChange(CalendarView.TimeGridWeek)}
                  fontWeight={400}
                  fontSize="15px"
                  py={2}
                  fontFamily='"Helvetica Neue", Helvetica, Arial, sans-serif'
                >
                  Weekly
                </MenuItem>
                <MenuItem
                  onClick={() => handleViewChange(CalendarView.DayGridMonth)}
                  fontWeight={400}
                  fontSize="15px"
                  py={2}
                  fontFamily='"Helvetica Neue", Helvetica, Arial, sans-serif'
                >
                  Monthly
                </MenuItem>
              </MenuList>
            </Portal>
          </Menu>
        </Flex>
      </Flex>
    )
  }

  //Non-mobile
  return (
    <Flex
      justify="space-between"
      align="center"
      mb={4}
      mt={4}
      wrap={{ base: 'wrap', md: 'nowrap' }}
      gap={{ base: 1, md: 3 }}
      zIndex={2}
    >
      <HStack spacing={{ base: 1, md: 3 }} minWidth={0}>
        {showSidebarMenuButton && (
          <IconButton
            ref={btnRef}
            icon={<SliderIcon />}
            aria-label="Open sidebar menu"
            onClick={onSidebarMenuClick}
            variant="outline"
            borderRadius="4px"
            minW="32px"
            px={2}
            bg="transparent"
            _hover={{ bg: 'rgba(45,84,232,0.04)', color: '#2D54E8' }}
            _focus={{ boxShadow: 'none' }}
          />
        )}
        <Text
          isTruncated
          minWidth={0}
          fontSize="24px"
          fontStyle="normal"
          color="#282828"
        >
          <span
            style={{
              fontWeight: 600,
              fontVariantNumeric: 'lining-nums',
              paddingRight: '4px'
            }}
          >
            {navigationDate.format('MMM YYYY')}
          </span>{' '}
          <span style={{ fontWeight: 400, fontVariantNumeric: 'lining-nums' }}>
            Calendar
          </span>
        </Text>
        <Button
          size="sm"
          fontSize="md"
          borderRadius="md"
          variant="outline"
          onClick={previousButtonClick}
          borderColor="#E2E8F0"
          minW="32px"
          h="32px"
          w="32px"
          p={0}
          _hover={{ bg: 'rgba(45,84,232,0.04)' }}
        >
          <ChevronLeft width={12} height={12} />
        </Button>
        <Button
          size="sm"
          fontSize="md"
          borderRadius="md"
          variant="outline"
          onClick={nextButtonClick}
          borderColor="#E2E8F0"
          minW="32px"
          h="32px"
          w="32px"
          p={0}
          _hover={{ bg: 'rgba(45,84,232,0.04)' }}
        >
          <ChevronRight width={12} height={12} />
        </Button>
        <Button
          variant="ghost"
          fontWeight="500"
          fontSize="14px"
          color="#6B7280"
          px={2}
          _hover={{ color: '#2D54E8', textDecoration: 'none' }}
          _focus={{ boxShadow: 'none' }}
          onClick={todayButtonClick}
        >
          Jump to today
        </Button>
      </HStack>
      <Menu isLazy>
        <MenuButton
          as={Button}
          rightIcon={<ChevronDown width={16} height={16} />}
          borderRadius="md"
          boxShadow="sm"
          bg="#fff"
          border="1px solid #E2E8F0"
          fontWeight={400}
          fontSize="15px"
          px={4}
          py={2}
          color="black"
          _hover={{ bg: 'rgba(45,84,232,0.04)' }}
        >
          {selectedView === CalendarView.DayGridMonth ? 'Monthly' : 'Weekly'}
        </MenuButton>
        <Portal>
          <MenuList
            boxShadow="lg"
            borderRadius="md"
            minW="120px"
            p={0}
            zIndex={9999}
            fontFamily='"Helvetica Neue", Helvetica, Arial, sans-serif'
          >
            <MenuItem
              onClick={() => handleViewChange(CalendarView.TimeGridWeek)}
              fontWeight={400}
              fontSize="15px"
              py={2}
              fontFamily='"Helvetica Neue", Helvetica, Arial, sans-serif'
            >
              Weekly
            </MenuItem>
            <MenuItem
              onClick={() => handleViewChange(CalendarView.DayGridMonth)}
              fontWeight={400}
              fontSize="15px"
              py={2}
              fontFamily='"Helvetica Neue", Helvetica, Arial, sans-serif'
            >
              Monthly
            </MenuItem>
          </MenuList>
        </Portal>
      </Menu>
    </Flex>
  )
}
